<template>
  <div>
    <v-card>
      <v-card-title class="text-h6">
        {{ $t('Supporting_Documents') }} {{ documents.length ? `(${documents.length})` : '(0)' }}
        <template v-if="canShowShowButton">
          <v-tooltip :text="show ? $t('Hide') : $t('Show')" location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon @click="show = !show" v-bind="props">{{ show ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            </template>
          </v-tooltip>
        </template>
      </v-card-title>
      <v-card-text v-if="show">
        <div v-if="gettingDocuments">{{ $t('Retrieving_Documents') }}...</div>
        <template v-else>
          <v-container class="hidden-md-and-up">
            <v-row dense v-if="documents.length">
              <v-col cols="12" sm="6" md="6" v-for="doc in documents" v-bind:key="doc.id">
                <v-card>
                  <v-container>
                    <v-row dense>
                      <v-col>
                        <div>
                          <a href="#" class="noUnder" @click.prevent="downloadDoc(doc)">
                            {{ doc.fileName }}
                          </a>
                        </div>
                        <div>{{ (doc.modifiedBy || {}).displayName }}</div>
                        <div>{{ $filters.DateTimeFilter(doc.modifiedByDate) }}</div>
                      </v-col>
                      <v-col cols="1">
                        <a href="#" class="noUnder" @click.prevent="setupDocDetails(doc)">
                          <v-icon>mdi-dots-horizontal</v-icon>
                        </a>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-col>
            </v-row>
            <v-row dense v-else>
              <v-col>
                {{ $t('No_Results') }}
              </v-col>
            </v-row>
          </v-container>
          <v-data-table class="hidden-sm-and-down" :headers="dataTableHeaders" :items="documents">
            <template v-slot:item="props">
              <tr>
                <td>
                  <a href="#" class="noUnder" @click.prevent="downloadDoc(props.item)">
                    {{ props.item.fileName }}
                  </a>
                </td>
                <td>{{ (props.item.modifiedBy || {}).displayName }}</td>
                <td>{{ $filters.DateTimeFilter(props.item.modifiedByDate) }}</td>
                <td>
                  <a href="#" class="noUnder" @click.prevent="setupDocDetails(props.item)">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </a>
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>

    <toaster ref="toaster"></toaster>

    <v-dialog v-model="showDocDetails" max-width="500">
      <v-card>
        <v-app-bar color="blue-grey" dense></v-app-bar>
        <v-card-title>{{ detailsDoc.fileName }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row dense>
              <v-col>
                <div>{{ $t('Created_By') }}: {{ (detailsDoc.insertedBy || {}).displayName }}</div>
                <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(detailsDoc.insertedByDate) }}</div>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <div>{{ $t('Modified_By') }}: {{ (detailsDoc.modifiedBy || {}).displayName }}</div>
                <div>{{ $t('Modified_On') }}: {{ $filters.DateTimeFilter(detailsDoc.modifiedByDate) }}</div>
              </v-col>
            </v-row>
            <strong>{{ $t('Versions') }}</strong>
            <template v-for="(version, index) in reversedDocVersions" :key="index">
              <v-row dense align="center">
                <v-col cols="1">
                  <a href="#" class="noUnder" @click.prevent="downloadDoc(detailsDoc, version.versionId)">
                    <v-icon>mdi-cloud-download-outline</v-icon>
                  </a>
                </v-col>
                <v-col>
                  <div>{{ $t('Created_By') }}: {{ (version.insertedBy || {}).displayName }}</div>
                  <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(version.insertedByDate) }}</div>
                  <div>
                    <pre style="white-space: pre-wrap;">{{ version.comment || '' }}</pre>
                  </div>
                </v-col>
              </v-row>
            </template>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn color="info" text @click="showDocDetails = false">{{ $t('Close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <loading-dialog ref="loadingDialog"></loading-dialog>
  </div>
</template>

<script>
import ExternalService from '@/services/external.service'
import Toaster from '@/components/Toaster'
import LoadingDialog from '@/components/externalMocForm/LoadingDialog'

export default {
  name: 'SupportingDocuments',
  components: {
    Toaster,
    LoadingDialog
  },
  data() {
    return {
      show: false,
      documents: [],
      gettingDocuments: false,
      showDocDetails: false,
      detailsDoc: {},
      dataTableHeaders: [
        { title: this.$t('File_Name'), key: 'fileName' },
        { title: this.$t('Modified_By'), key: 'modifiedBy' },
        { title: this.$t('Modified_On'), key: 'modifiedByDate' },
        { title: '', sortable: false }
      ]
    }
  },
  props: {
    moc: {
      type: Object
    }
  },
  computed: {
    reversedDocVersions() {
      if (!this.detailsDoc.versions) return []
      return this.detailsDoc.versions.slice().reverse()
    },
    canShowShowButton: function() {
      return this.documents.length > 0
    }
  },
  methods: {
    getFiles: function() {
      if (!this.moc.mocId) return
      if (!this.gettingDocuments) {
        // get list of docs in S3 for this moc
        this.gettingDocuments = true
        return ExternalService.getMocSupportingDocs(this.moc.mocId)
          .then(data => {
            this.documents = data
          })
          .catch(error => {
            console.error(error)
            this.$refs.loadingDialog.error({ message: this.$t('Error_GetDocuments') })
          })
          .finally(() => {
            this.gettingDocuments = false
          })
      }
    },

    downloadDoc: function(doc, versionId) {
      this.$refs.loadingDialog.show()
      return new Promise((resolve, reject) => {
        ExternalService.downloadSupportingDoc(this.moc.mocId, doc.fileName, versionId)
          .then(url => {
            this.$refs.loadingDialog.hide()
            const a = document.createElement('a')
            a.download = doc.fileName
            a.href = url
            a.style.display = 'none'
            a.rel = 'noopener'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            resolve()
          })
          .catch(error => {
            console.error(error)
            this.$refs.loadingDialog.error({ message: this.$t('Error_DownloadDocument') })
            reject(error)
          })
          .finally(() => {
            this.$refs.loadingDialog.hide()
          })
      })
    },
    setupDocDetails: function(doc) {
      this.detailsDoc = doc
      this.showDocDetails = true
    }
  },
  created() {
    this.getFiles()
  }
}
</script>

<style scoped>
.noUnder {
  text-decoration: none;
}
.title {
  font-size: 0.95rem !important;
  font-weight: 400;
}
.dropBox {
  border: 1px solid gray;
}
</style>
